/**
 * Utility functions for managing campaign parameters
 * Handles detection, storage, and retrieval of campaign parameters from URL and localStorage
 */

const CAMPAIGN_STORAGE_KEY = 'dante_campaign_param';
const CAMPAIGN_LANDING_PAGE_VALUE = '1$_lp';

/**
 * Check if the current URL contains the campaign parameter
 * @param {string} url - Optional URL to check, defaults to current window location
 * @returns {string|null} The campaign parameter value if found, null otherwise
 */
export const getCampaignFromUrl = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('campaign');
  } catch (error) {
    console.warn('Error parsing URL for campaign parameter:', error);
    return null;
  }
};

/**
 * Check if the campaign parameter matches the landing page campaign
 * @param {string} campaignValue - The campaign parameter value to check
 * @returns {boolean} True if it matches the landing page campaign
 */
export const isLandingPageCampaign = (campaignValue) => {
  return campaignValue === CAMPAIGN_LANDING_PAGE_VALUE;
};

/**
 * Store campaign parameter in localStorage
 * @param {string} campaignValue - The campaign parameter value to store
 */
export const storeCampaignParam = (campaignValue) => {
  if (!campaignValue) return;
  
  try {
    localStorage.setItem(CAMPAIGN_STORAGE_KEY, campaignValue);
  } catch (error) {
    console.warn('Error storing campaign parameter:', error);
  }
};

/**
 * Get stored campaign parameter from localStorage
 * @returns {string|null} The stored campaign parameter value if found, null otherwise
 */
export const getStoredCampaignParam = () => {
  try {
    return localStorage.getItem(CAMPAIGN_STORAGE_KEY);
  } catch (error) {
    console.warn('Error retrieving stored campaign parameter:', error);
    return null;
  }
};

/**
 * Clear stored campaign parameter from localStorage
 */
export const clearStoredCampaignParam = () => {
  try {
    localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
  } catch (error) {
    console.warn('Error clearing stored campaign parameter:', error);
  }
};

/**
 * Check if user should see the campaign modal
 * This checks both URL and stored campaign parameters
 * @param {string} url - Optional URL to check, defaults to current window location
 * @returns {boolean} True if user should see the campaign modal
 */
export const shouldShowCampaignModal = (url = window.location.href) => {
  // First check current URL
  const urlCampaign = getCampaignFromUrl(url);

  if (isLandingPageCampaign(urlCampaign)) {
    // Store it for later use
    storeCampaignParam(urlCampaign);
    return true;
  }

  // Then check stored campaign
  const storedCampaign = getStoredCampaignParam();

  return isLandingPageCampaign(storedCampaign);
};

/**
 * Initialize campaign tracking on page load
 * This should be called early in the application lifecycle
 * @param {string} url - Optional URL to check, defaults to current window location
 */
export const initializeCampaignTracking = (url = window.location.href) => {
  const campaignParam = getCampaignFromUrl(url);
  if (campaignParam) {
    storeCampaignParam(campaignParam);
  }
};

/**
 * Get the current active campaign parameter
 * Checks URL first, then localStorage
 * @param {string} url - Optional URL to check, defaults to current window location
 * @returns {string|null} The active campaign parameter value if found, null otherwise
 */
export const getActiveCampaignParam = (url = window.location.href) => {
  // Check URL first (takes priority)
  const urlCampaign = getCampaignFromUrl(url);
  if (urlCampaign) {
    return urlCampaign;
  }

  // Fallback to stored campaign
  return getStoredCampaignParam();
};

/**
 * Add campaign parameter to a URL if one is active
 * @param {string} baseUrl - The base URL to add the campaign parameter to
 * @returns {string} The URL with campaign parameter added if active, otherwise the original URL
 */
export const addCampaignToUrl = (baseUrl) => {
  const activeCampaign = getActiveCampaignParam();
  if (!activeCampaign) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('campaign', activeCampaign);
    return url.pathname + url.search;
  } catch (error) {
    console.warn('Error adding campaign to URL:', error);
    return baseUrl;
  }
};
